import {useState, useEffect, useCallback} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Formik, Form} from 'formik';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import Container from '../../components/common/layout/Container';
import {Button, ButtonWithLoader} from '../common/Button';
import ActionButtons from '../common/layout/ActionButtons';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import EnsurePermission from '../common/EnsurePermission';
import {addUserData} from '../../constants/userData';
import {CreateUserProps, GroupListProps, OptionsProp} from '../../constants/types';
import {addSystemUserValidationSchema, addUserValidationSchema} from '../../constants/validationSchemes';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {TextField, AutocompleteField} from '../common/formik/fields';
import {useTranslation} from 'react-i18next';
import {getAvailableGroups, getGroupsByTenant} from '../../api/groups';
import {arrayToOptions, getTenantId} from './helpers';
import {FormGroup} from '@mui/material';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const initialValues = {
  email: '',
  groups: []
};

const systemInitialValues = {
  email: '',
  groups: [],
  tenantId: null
};

const AddUser = () => {
  const {t} = useTranslation(['systemConfiguration', 'user']);
  const history = useHistory();
  const {pathname} = useLocation();
  const dispatch = useAppDispatch();
  const {availableTenants, activeTenant} =
    useAppSelector(({permissions}) => permissions);
  const [isLoading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GroupListProps[]>([]);
  const [apiValidationError, setApiValidationError] =
   useState<Nullable<Dictionary<string[]>>>(null);
  const [groupOptions, setGroupOptions] = useState<OptionsProp[]>([]);
  const [tenantOptions, setTenantOptions] = useState<OptionsProp[]>([]);
  const [tenantOption, setTenantOption] =
    useState<Nullable<OptionsProp>>(null);
  const entity = addUserData?.[pathname];
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const getAllGroups = useCallback(async () => {
    setLoading(true);
    const tenantId = getTenantId(activeTenant, availableTenants);

    if (tenantId) {
      try {
        const response = entity.isSystemConfiguration ?
          await getGroupsByTenant(tenantId) :
          await getAvailableGroups();

        setGroups(response);
      } catch (error) {
        addErrorToast(error?.message);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const getAllGroupsByTenant = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await getGroupsByTenant(Number(tenantOption));
      setGroups(response);
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  }, [tenantOption]);

  useEffect(() => {
    dispatch(setTitle(''));
    entity.isTenantConfiguration && getAllGroups();
  }, []);

  useEffect(() => {
    if (!groups) return;

    setGroupOptions(arrayToOptions(groups));
  }, [groups]);

  useEffect(() => {
    if (availableTenants) {
      setTenantOptions(arrayToOptions(availableTenants));
    }
  }, [availableTenants]);

  useEffect(() => {
    if (tenantOption) {
      getAllGroupsByTenant();
    }
  }, [tenantOption]);

  const onSubmit = async (values: CreateUserProps) => {
    const sanitizeValues = sanitize(values);
    setApiValidationError(null);

    try {
      await entity.add(sanitizeValues);
      addSuccessToast(t('user:toasts:createUserMessage'));
      history.push(entity?.returnRoute);
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'user');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const handleTenantChange = useCallback((tenant) => {
    if (tenant) {
      setTenantOption(tenant);
    }
  }, []);

  const cancelCreation = () => {
    history.push(entity?.returnRoute);
  };

  return (
    <Container header={entity?.header}>
      <h2 className="subheader">{entity?.subheader}</h2>

      <Formik
        initialValues={entity.isSystemConfiguration ? systemInitialValues :
          initialValues}
        validationSchema={entity.isSystemConfiguration ?
          addSystemUserValidationSchema : addUserValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, submitForm,
          isSubmitting, isValid, dirty, setFieldValue}) => {
          useOnUnsavedChanges(dirty);
          const isDisabled = !isValid || isSubmitting || isLoading;

          return (
            <>
              <div className="creation-form-wrapper">
                <Form className="form user-add__form">
                  <FormGroup>
                    <TextField
                      name="email"
                      label={t('addUser.emailLabel')}
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setFieldTouched('email', true)}
                      disabled={isSubmitting}
                      apiValidationError={apiValidationError}
                      required
                    />
                  </FormGroup>

                  {entity.isSystemConfiguration && (
                    <FormGroup>
                      <AutocompleteField
                        name="tenantId"
                        label={t('addUser.tenantLabel')}
                        options={tenantOptions}
                        required
                        disableClearable
                        disabled={isSubmitting || isLoading}
                        onChangeCallback={(value) => {
                          handleTenantChange(value);
                          setFieldValue('tenantId', value);
                          setFieldValue('groups', []);
                        }}
                        onFocus={() => setApiValidationError(null)}
                        apiValidationError={apiValidationError}
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <AutocompleteField
                      name="groups"
                      label={t('addUser.groupLabel')}
                      options={groupOptions}
                      required
                      multiple
                      disableCloseOnSelect
                      disabled={isSubmitting || isLoading}
                      onFocus={() => setApiValidationError(null)}
                      apiValidationError={apiValidationError}
                      fieldDescription={t('addUser.moreGroups')}
                    />
                  </FormGroup>
                </Form>
              </div>
              <ActionButtons>
                <EnsurePermission
                  elementPermissions={entity?.permissions}>
                  <ButtonWithLoader
                    testId="create-group"
                    label={t('addUser.createUser')}
                    disabled={isDisabled}
                    isSubmitting={isSubmitting}
                    onClick={submitForm}
                  />
                </EnsurePermission>

                <Button
                  onClick={cancelCreation}
                  label={t('addUser.cancel')}
                  variant="outlined"
                  color="secondary"
                />
              </ActionButtons>
            </>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AddUser;
